import { useState, useEffect, useMemo, useCallback, memo, lazy, Suspense, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, ShoppingCart, Trash2, CreditCard, Printer, Upload, Edit, Search, Filter, Banknote, LayoutGrid, List, Percent, DollarSign, GripVertical, Package, AlertTriangle, FileDown, Paperclip, Save, Eye, FileText, MessageCircle, Truck, MapPin, X, User, Calendar, Cloud, CheckCircle, Loader2, ExternalLink, ClipboardList, ChevronRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DndContext, DragOverlay, pointerWithin, rectIntersection, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { formatCPF, formatPhone, validateCPF, formatDateDisplay, formatCNPJ, validateCNPJ, formatCPFCNPJ, validateCPFCNPJ, normalizeText, searchMatch, formatCurrency } from '@/utils/formatters';
import { useWebSocket } from '@/hooks/useWebSocket';
import QuotesTab from '@/components/quotes/QuotesTab';
import NewOrderModal from '@/components/orders/NewOrderModal';
import CalculatorsTab from '@/components/calculators/CalculatorsTab';
import { EntregasTab } from '@/components/orders/EntregasTab';
import ProjetadosTab from '@/components/orders/ProjetadosTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Constante para limitar itens nos dropdowns
const MAX_DROPDOWN_ITEMS = 50;

// Lista de estados brasileiros
const ESTADOS_BR = [
  { sigla: 'AC', nome: 'Acre' }, { sigla: 'AL', nome: 'Alagoas' }, { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' }, { sigla: 'BA', nome: 'Bahia' }, { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' }, { sigla: 'ES', nome: 'Espírito Santo' }, { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' }, { sigla: 'MT', nome: 'Mato Grosso' }, { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' }, { sigla: 'PA', nome: 'Pará' }, { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' }, { sigla: 'PE', nome: 'Pernambuco' }, { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' }, { sigla: 'RN', nome: 'Rio Grande do Norte' }, { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' }, { sigla: 'RR', nome: 'Roraima' }, { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' }, { sigla: 'SE', nome: 'Sergipe' }, { sigla: 'TO', nome: 'Tocantins' }
];

const STATUS_OPTIONS = ['Aguardando', 'Em Produção', 'Pronto', 'Entregue Parcial', 'Entregue', 'Finalizado'];
const KANBAN_COLUMNS = ['Aguardando', 'Em Produção', 'Pronto', 'Entregue Parcial', 'Entregue', 'Finalizado'];
const BANDEIRAS = ['visa', 'mastercard', 'elo', 'amex'];
const STATUS_FINANCEIRO_OPTIONS = ['Pendente', 'Pago 50%', 'Pago Total'];

const STATUS_COLORS = {
  'Aguardando': 'bg-yellow-500/10 text-yellow-500 border-yellow-500',
  'Pendente': 'bg-yellow-500/10 text-yellow-500 border-yellow-500',
  'Em Produção': 'bg-blue-500/10 text-blue-500 border-blue-500',
  'Produção': 'bg-blue-500/10 text-blue-500 border-blue-500',
  'Pronto': 'bg-purple-500/10 text-purple-500 border-purple-500',
  'Entregue Parcial': 'bg-amber-500/10 text-amber-600 border-amber-500',
  'Entregue': 'bg-orange-500/10 text-orange-500 border-orange-500',
  'Finalizado': 'bg-green-500/10 text-green-500 border-green-500',
  'Cancelado': 'bg-destructive/10 text-destructive border-destructive'
};

const FINANCEIRO_COLORS = {
  'Pendente': 'bg-destructive/10 text-destructive border-destructive',
  'Pago 50%': 'bg-yellow-500/10 text-yellow-500 border-yellow-500',
  'Pago Total': 'bg-green-500/10 text-green-500 border-green-500'
};

// Função para gerar link do WhatsApp com mensagem
const getWhatsAppLink = (telefone, mensagem = '') => {
  if (!telefone) return null;
  const numero = telefone.replace(/\D/g, '');
  const numeroFormatado = numero.startsWith('55') ? numero : `55${numero}`;
  const msg = encodeURIComponent(mensagem);
  return `https://wa.me/${numeroFormatado}${msg ? `?text=${msg}` : ''}`;
};

// Função para gerar link do Google Maps com endereço
const getGoogleMapsLink = (endereco) => {
  if (!endereco) return null;
  const enderecoFormatado = encodeURIComponent(endereco);
  return `https://www.google.com/maps/search/?api=1&query=${enderecoFormatado}`;
};

// Mensagens pré-formatadas para pedidos
const getMensagemEntregaACaminho = (nomeCliente) => 
  `Olá ${nomeCliente}! Tudo bem? Aqui é da Serraria Guedes. Estou entrando em contato para te informar que estamos indo fazer a entrega do seu pedido.`;

const getMensagemPedidoPronto = (nomeCliente) => 
  `Olá ${nomeCliente}! Aqui é da Serraria Guedes. Estamos passando para informar que seu pedido ficou pronto e iremos fazer a entrega `;

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [chartFilter, setChartFilter] = useState('anual');
  const [editingOrder, setEditingOrder] = useState(null);
  const [uploadingOrderId, setUploadingOrderId] = useState(null);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [activeTab, setActiveTab] = useState('pedidos'); // 'pedidos' ou 'orcamentos'
  
  // Estados para paginação e otimização
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterMonths, setFilterMonths] = useState(3); // Carregar últimos 3 meses por padrão

  // Carregamento sob demanda de Finalizados (coluna Kanban / aba Lista)
  const [finalizadosLoaded, setFinalizadosLoaded] = useState(false);
  const [finalizadosLoading, setFinalizadosLoading] = useState(false);
  const [finalizadosPage, setFinalizadosPage] = useState(1);
  const [finalizadosTotal, setFinalizadosTotal] = useState(0);
  const finalizadosLoadedRef = useRef(false);
  
  // Estados para integração Omie
  const [omieStatus, setOmieStatus] = useState(null);
  const [sendingToOmie, setSendingToOmie] = useState(null);
  const [faturandoOmie, setFaturandoOmie] = useState(null);

  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' ou 'list'
  const [activeId, setActiveId] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null); // Modal de visualização do pedido
  const [viewingAttachments, setViewingAttachments] = useState(null); // Modal de anexos
  const [productCategories, setProductCategories] = useState([]);
  const [proximoNumeroPedido, setProximoNumeroPedido] = useState(1);
  
  // Estados para criar OS com seleção de itens
  const [osModalOpen, setOsModalOpen] = useState(false);
  const [osSelectedPedido, setOsSelectedPedido] = useState(null);
  const [osSelectedItens, setOsSelectedItens] = useState([]);
  const [creatingOS, setCreatingOS] = useState(false);

  // Estados para modal de pagamento com campos de cartão
  const [paymentFormData, setPaymentFormData] = useState({
    forma_pagamento: 'PIX',
    valor: '',
    conta_id: '',
    observacao: '',
    // Campos específicos de cartão
    bandeira: '',
    parcelas: '1',
    nsu: ''
  });

  // Estado para menu mobile de movimentação de cards
  const [showMobileMenu, setShowMobileMenu] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Custom collision detection that ONLY considers Kanban columns, not cards
  // Uses pointerWithin for more accurate detection based on pointer position
  const columnOnlyCollision = (args) => {
    const { droppableContainers, pointerCoordinates } = args;
    
    if (!pointerCoordinates) {
      return [];
    }

    // Filter to only include column droppables (KANBAN_COLUMNS status names)
    const columnContainers = droppableContainers.filter(
      container => KANBAN_COLUMNS.includes(container.id)
    );

    // Use pointerWithin on columns only - more precise for kanban boards
    const collisions = pointerWithin({
      ...args,
      droppableContainers: columnContainers,
    });

    return collisions;
  };

  // Calcular próximo número de pedido
  useEffect(() => {
    if (orders.length > 0) {
      const numeros = orders
        .map(o => {
          const match = o.numero_pedido?.match(/PED-(\d+)/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter(n => n > 0);
      const max = Math.max(0, ...numeros);
      setProximoNumeroPedido(max + 1);
    }
  }, [orders]);

  useEffect(() => {
    fetchData();
    // Pré-carrega apenas o total de Finalizados para o badge da coluna
    axios.get(`${API}/orders`, { params: { status: 'Finalizado', light: true, page: 1, limit: 1 } })
      .then(res => setFinalizadosTotal(res.data?.total || 0))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce da busca (350ms) + AbortController para cancelar requisições antigas
  const searchDebounceRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchMountedRef = useRef(false);
  useEffect(() => {
    // Pular a primeira chamada (mount) — o fetchData inicial já cobre esse caso.
    if (!searchMountedRef.current) {
      searchMountedRef.current = true;
      return;
    }
    // Ao mudar o termo de busca, agenda um refetch com o filtro no backend
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      fetchData(1, false, { searchOverride: searchTerm });
    }, 350);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Quando o escopo muda (Finalizados carregados/recolhidos) com busca ativa, refaz a consulta
  useEffect(() => {
    if (!searchMountedRef.current) return;
    if (!searchTerm || !searchTerm.trim()) return;
    fetchData(1, false, { searchOverride: searchTerm, includeFinalizados: finalizadosLoaded, silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalizadosLoaded]);

  // WebSocket para atualizações em tempo real
  const lastLocalUpdateRef = useRef(0);
  
  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === 'data_updated') {
      console.log('Pedidos - dados atualizados via WebSocket:', message.data);
      
      // Evitar recarregar se acabamos de fazer uma atualização local (últimos 2 segundos)
      const now = Date.now();
      if (now - lastLocalUpdateRef.current < 2000) {
        console.log('Pedidos - ignorando WebSocket (atualização local recente)');
        return;
      }
      
      fetchData();
      
      // Mostrar notificação visual se houver mensagem
      if (message.message) {
        const entity = message.data?.entity;
        // Mostrar toast apenas para entidades relevantes nesta página
        if (entity === 'orders' || entity === 'quotes') {
          toast.info(message.message, {
            description: 'Sincronizado automaticamente',
            duration: 4000,
          });
        }
      }
    }
  }, []);

  useWebSocket(handleWebSocketMessage, []);

  const fetchData = useCallback(async (page = 1, appendData = false, opts = {}) => {
    try {
      // Loading "silencioso" quando é um refetch disparado por busca/escopo
      // (não bloqueia a tela, só atualiza a lista no background)
      const silent = opts.silent === true || opts.searchOverride !== undefined;
      if (page === 1 && !silent) setLoading(true);
      else if (page > 1) setLoadingMore(true);

      // Cancela requisição de pedidos anterior (evita respostas antigas sobrescrevendo)
      if (searchAbortRef.current) {
        try { searchAbortRef.current.abort(); } catch (e) {}
      }
      const controller = new AbortController();
      searchAbortRef.current = controller;

      const activeSearch = (opts.searchOverride !== undefined ? opts.searchOverride : searchTerm) || '';
      const includeFinalizadosNow = opts.includeFinalizados === true || finalizadosLoadedRef.current;

      // Parâmetros da busca de pedidos
      const params = { light: true, all_data: true };
      if (activeSearch && activeSearch.trim().length > 0) {
        params.search = activeSearch.trim();
        // Escopo da busca segue a visibilidade dos Finalizados
        params.scope = includeFinalizadosNow ? 'all' : 'active';
      } else if (!includeFinalizadosNow) {
        // Carregamento normal: exclui Finalizado (traz tudo do restante)
        params.exclude_status = 'Finalizado';
      }

      const ordersRes = await axios.get(`${API}/orders`, {
        params,
        signal: controller.signal,
      });

      // Resposta pode ser array (light/all_data) ou objeto paginado
      let newOrders, total, total_pages;
      if (Array.isArray(ordersRes.data)) {
        newOrders = ordersRes.data;
        total = newOrders.length;
        total_pages = 1;
      } else {
        newOrders = ordersRes.data.orders || [];
        total = ordersRes.data.total || newOrders.length;
        total_pages = ordersRes.data.total_pages || 1;
      }

      setOrders(newOrders);
      setPagination({ page: 1, total, totalPages: total_pages });
      calculateChartData(newOrders);

      // Buscar dados secundários apenas na primeira carga (não bloqueante)
      if (page === 1) {
        axios.get(`${API}/customers`, { params: { all_data: true } })
          .then(res => {
            const data = Array.isArray(res.data) ? res.data : (res.data.customers || []);
            setCustomers(data);
          })
          .catch(e => console.error('Erro ao carregar clientes:', e));

        Promise.all([
          axios.get(`${API}/bank-accounts`),
          axios.get(`${API}/settings/categories/product`)
        ]).then(([accountsRes, categoriesRes]) => {
          setBankAccounts(accountsRes.data);
          setProductCategories(categoriesRes.data.categories || []);
        }).catch(e => console.error('Erro ao carregar dados secundários:', e));

        axios.get(`${API}/products`, { params: { all_data: true } })
          .then(res => {
            const data = Array.isArray(res.data) ? res.data : (res.data.products || []);
            setProducts(data);
          })
          .catch(e => console.error('Erro ao carregar produtos:', e));

        axios.get(`${API}/omie/status`).then(res => {
          setOmieStatus(res.data);
        }).catch(() => console.log('Omie não configurado'));
      }

    } catch (error) {
      // Requests cancelados via AbortController não são erros reais
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchTerm]);
  
  // Função para carregar mais pedidos
  const loadMoreOrders = useCallback(() => {
    if (pagination.page < pagination.totalPages && !loadingMore) {
      fetchData(pagination.page + 1, true);
    }
  }, [pagination, loadingMore, fetchData]);

  // Carrega/anexa pedidos com status "Finalizado" sob demanda (paginado, 50 por página)
  const loadFinalizados = useCallback(async (nextPage = 1) => {
    try {
      setFinalizadosLoading(true);
      const res = await axios.get(`${API}/orders`, {
        params: { status: 'Finalizado', light: true, page: nextPage, limit: 50 }
      });
      const data = res.data || {};
      const items = data.orders || [];
      setFinalizadosTotal(data.total || items.length);
      setFinalizadosPage(nextPage);
      // Merge sem duplicar
      setOrders(prev => {
        const ids = new Set(prev.map(o => o.id));
        const merged = [...prev];
        for (const o of items) {
          if (!ids.has(o.id)) merged.push(o);
        }
        return merged;
      });
      finalizadosLoadedRef.current = true;
      setFinalizadosLoaded(true);
    } catch (e) {
      toast.error('Erro ao carregar finalizados');
    } finally {
      setFinalizadosLoading(false);
    }
  }, []);

  // Funções para integração Omie
  const handleSyncProductsAndSend = async (orderId) => {
    setSendingToOmie(orderId);
    try {
      // Primeiro, sincronizar os produtos do pedido
      const syncResponse = await axios.post(`${API}/omie/pedidos/${orderId}/sincronizar-produtos`);
      if (syncResponse.data.sincronizados > 0) {
        toast.success(`${syncResponse.data.sincronizados} produto(s) sincronizado(s) com o Omie`);
      }
      if (syncResponse.data.erros && syncResponse.data.erros.length > 0) {
        toast.error(`Erros ao sincronizar: ${syncResponse.data.erros.join(', ')}`);
        setSendingToOmie(null);
        return;
      }
      
      // Agora enviar o pedido
      const response = await axios.post(`${API}/omie/pedidos/enviar/${orderId}`);
      if (response.data.success) {
        toast.success(`Pedido enviado! Código Omie: ${response.data.codigo_pedido_omie}`);
        fetchData();
      } else {
        toast.error(response.data.error || 'Erro ao enviar');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao enviar para Omie';
      // Se o erro for sobre produtos não sincronizados, oferecer sincronizar
      if (detail.includes('não estão sincronizados')) {
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setSendingToOmie(null);
    }
  };

  const handleSendToOmie = async (orderId) => {
    setSendingToOmie(orderId);
    try {
      const response = await axios.post(`${API}/omie/pedidos/enviar/${orderId}`);
      if (response.data.success) {
        toast.success(`Pedido enviado! Código Omie: ${response.data.codigo_pedido_omie}`);
        fetchData();
      } else {
        toast.error(response.data.error || 'Erro ao enviar');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao enviar para Omie';
      // Se o erro for sobre produtos não sincronizados, perguntar se quer sincronizar
      if (detail.includes('não estão sincronizados')) {
        if (window.confirm(`${detail}\n\nDeseja sincronizar os produtos automaticamente e enviar o pedido?`)) {
          await handleSyncProductsAndSend(orderId);
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setSendingToOmie(null);
    }
  };

  const handleFaturarOmie = async (orderId) => {
    setFaturandoOmie(orderId);
    try {
      const response = await axios.post(`${API}/omie/pedidos/faturar/${orderId}`);
      if (response.data.success) {
        toast.success(`NF-e gerada! Número: ${response.data.numero_nfe}`);
        fetchData();
      } else {
        const errorMsg = response.data.error || 'Erro ao faturar';
        // Verificar se é erro de UF/Estado
        if (errorMsg.toLowerCase().includes('uf') || errorMsg.toLowerCase().includes('estado')) {
          toast.error(errorMsg, { duration: 8000, description: 'Edite o cliente e adicione o Estado (UF) no endereço' });
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Erro ao faturar no Omie';
      // Verificar se é erro de UF/Estado para mostrar mensagem mais clara
      if (errorDetail.toLowerCase().includes('uf') || errorDetail.toLowerCase().includes('estado')) {
        toast.error(errorDetail, { 
          duration: 10000,
          description: 'Vá em Clientes, edite o cadastro e adicione o Estado (ex: PB, SP, RJ)'
        });
      } else {
        toast.error(errorDetail, { duration: 6000 });
      }
    } finally {
      setFaturandoOmie(null);
    }
  };

  const handleGetDanfe = async (orderId) => {
    try {
      const response = await axios.get(`${API}/omie/pedidos/${orderId}/danfe`);
      if (response.data.success && response.data.url_danfe) {
        window.open(response.data.url_danfe, '_blank');
      } else {
        toast.error(response.data.error || 'DANFE não disponível');
      }
    } catch (error) {
      toast.error('Erro ao obter DANFE');
    }
  };

  const calculateChartData = (ordersData) => {
    const monthlyData = {};
    const now = new Date();
    
    ordersData.forEach(order => {
      const orderDate = new Date(order.data_pedido);
      const month = order.data_pedido.substring(0, 7);
      
      // Filtrar baseado no filtro selecionado
      if (chartFilter === 'mes_atual' && 
          (orderDate.getMonth() !== now.getMonth() || orderDate.getFullYear() !== now.getFullYear())) {
        return;
      }
      
      if (!monthlyData[month]) {
        monthlyData[month] = { vendas: 0, valor: 0 };
      }
      monthlyData[month].vendas += 1;
      monthlyData[month].valor += order.total_venda;
    });
    
    const chartArray = Object.keys(monthlyData).sort().map(month => ({
      mes: month,
      vendas: monthlyData[month].vendas,
      valor: monthlyData[month].valor
    }));
    
    setChartData(chartArray);
  };

  useEffect(() => {
    if (orders.length > 0) {
      calculateChartData(orders);
    }
  }, [chartFilter]);

  const handleEdit = (order) => {
    setEditingOrder(order);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este pedido?')) return;
    try {
      await axios.delete(`${API}/orders/${id}`);
      toast.success('Pedido excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir pedido');
    }
  };

  // Abrir modal para selecionar itens da OS
  const handleAbrirModalOS = (pedido) => {
    setOsSelectedPedido(pedido);
    // Pré-selecionar todos os itens
    const itensIds = pedido.produtos?.map((_, idx) => idx) || [];
    setOsSelectedItens(itensIds);
    setOsModalOpen(true);
  };

  // Criar Ordem de Serviço com itens selecionados
  const handleCriarOS = async () => {
    if (!osSelectedPedido || osSelectedItens.length === 0) {
      toast.error('Selecione pelo menos um item');
      return;
    }
    
    try {
      setCreatingOS(true);
      // Filtrar apenas os produtos selecionados
      const itensSelecionados = osSelectedPedido.produtos
        .filter((_, idx) => osSelectedItens.includes(idx))
        .map(p => ({
          produto_id: p.produto_id,
          produto_nome: p.nome || 'Produto sem nome',
          quantidade: parseInt(p.quantidade) || 1,
          madeira: p.tipo_madeira,
          acabamento: p.acabamento,
          observacoes_tecnicas: p.observacoes
        }));
      
      const response = await axios.post(`${API}/ordens-servico`, {
        pedido_id: osSelectedPedido.id,
        numero_pedido: osSelectedPedido.numero_pedido,
        cliente_nome: osSelectedPedido.cliente_nome,
        cliente_id: osSelectedPedido.cliente_id,
        itens: itensSelecionados,
        observacoes_pedido: osSelectedPedido.observacoes,
        prazo: osSelectedPedido.data_entrega
      });
      
      const os = response.data;
      toast.success(`OS-${os.numero_os?.toString().padStart(4, '0')} criada com ${itensSelecionados.length} item(s)!`);
      setOsModalOpen(false);
      setOsSelectedPedido(null);
      setOsSelectedItens([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar Ordem de Serviço');
    } finally {
      setCreatingOS(false);
    }
  };

  // Toggle seleção de item da OS
  const toggleOsItem = (idx) => {
    setOsSelectedItens(prev => 
      prev.includes(idx) 
        ? prev.filter(i => i !== idx)
        : [...prev, idx]
    );
  };

  const handleUpdateStatus = async (orderId, status, status_financeiro) => {
    try {
      // Marcar que fizemos uma atualização local
      lastLocalUpdateRef.current = Date.now();
      
      // Atualização otimista - atualiza localmente primeiro para evitar flicker
      if (status) {
        setOrders(prev => prev.map(order => 
          order.id === orderId ? { ...order, status } : order
        ));
      }
      if (status_financeiro) {
        setOrders(prev => prev.map(order => 
          order.id === orderId ? { ...order, status_financeiro } : order
        ));
      }
      
      await axios.put(`${API}/orders/${orderId}/status`, { 
        status: status || undefined,
        status_financeiro: status_financeiro || undefined
      });
      toast.success('Status atualizado!');
      // Não chamar fetchData() aqui - o WebSocket já vai atualizar se necessário
    } catch (error) {
      toast.error('Erro ao atualizar status');
      // Em caso de erro, recarregar dados para reverter a atualização otimista
      fetchData();
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) {
      console.log('[DragEnd] Nenhum destino válido');
      return;
    }

    const orderId = active.id;
    let newStatus = over.id;

    console.log('[DragEnd] Active ID:', orderId, 'Over ID:', newStatus);

    // Check if over.id is a valid Kanban column (status)
    // If not, it means we're dropping over another card, not directly on the column
    if (!KANBAN_COLUMNS.includes(newStatus)) {
      // Find the order we're dropping onto and get its status
      const targetOrder = orders.find(o => o.id === newStatus);
      if (targetOrder) {
        newStatus = targetOrder.status;
        console.log('[DragEnd] Dropped on card, using its column status:', newStatus);
      } else {
        console.log('[DragEnd] Could not determine target column');
        return;
      }
    }

    console.log('[DragEnd] Movendo pedido:', orderId, 'para status:', newStatus);

    // Find the order being dragged
    const order = orders.find(o => o.id === orderId);
    if (!order) {
      console.log('[DragEnd] Pedido não encontrado');
      return;
    }
    
    if (order.status === newStatus) {
      console.log('[DragEnd] Status é o mesmo, ignorando');
      return;
    }

    // Update status
    try {
      await handleUpdateStatus(orderId, newStatus, null);
      console.log('[DragEnd] Status atualizado com sucesso');
    } catch (error) {
      console.error('[DragEnd] Erro ao atualizar status:', error);
      toast.error('Erro ao mover pedido');
    }
  };

  // Função para mover pedido via menu mobile
  const handleMoveOrder = async (order, newStatus) => {
    setShowMobileMenu(null);
    if (order.status === newStatus) return;
    
    try {
      await handleUpdateStatus(order.id, newStatus, null);
    } catch (error) {
      console.error('Erro ao mover pedido:', error);
      toast.error('Erro ao mover pedido');
    }
  };

  const handleRegistrarPagamento = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/create-transaction`);
      toast.success('Pagamento registrado no financeiro!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar pagamento');
    }
  };

  const handleDeletePagamento = async (orderId, paymentId) => {
    if (!window.confirm('Deseja realmente excluir este pagamento? A transação financeira também será removida.')) return;
    try {
      await axios.delete(`${API}/orders/${orderId}/payments/${paymentId}`);
      toast.success('Pagamento excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir pagamento');
    }
  };

  // Função para registrar pagamento com dados de cartão
  const handleSubmitPayment = async (orderId) => {
    const { forma_pagamento, valor, conta_id, observacao, bandeira, parcelas, nsu } = paymentFormData;
    
    if (!valor || parseFloat(valor) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    // Validar campos de cartão
    if (forma_pagamento === 'Débito' || forma_pagamento === 'Crédito') {
      if (!bandeira) {
        toast.error('Selecione a bandeira do cartão');
        return;
      }
      if (!nsu) {
        toast.error('Informe o NSU da transação');
        return;
      }
    }

    let contaId = conta_id;
    
    // Se não selecionou conta e é pagamento com cartão, usar conta de cartões
    if (!contaId && (forma_pagamento === 'Débito' || forma_pagamento === 'Crédito')) {
      const cardAcc = bankAccounts.find(a => a.conta_cartao);
      contaId = cardAcc ? cardAcc.id : null;
    }
    
    try {
      await axios.post(`${API}/orders/${orderId}/add-payment`, {
        forma_pagamento,
        valor: parseFloat(valor),
        conta_id: contaId || undefined,
        observacao: observacao || undefined,
        // Dados específicos de cartão
        bandeira: (forma_pagamento === 'Débito' || forma_pagamento === 'Crédito') ? bandeira : undefined,
        parcelas: forma_pagamento === 'Crédito' ? parseInt(parcelas) : undefined,
        nsu: (forma_pagamento === 'Débito' || forma_pagamento === 'Crédito') ? nsu : undefined
      });
      
      toast.success('Pagamento registrado!');
      
      // Resetar formulário
      setPaymentFormData({
        forma_pagamento: 'PIX',
        valor: '',
        conta_id: '',
        observacao: '',
        bandeira: '',
        parcelas: '1',
        nsu: ''
      });
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar pagamento');
    }
  };

  // Resetar formulário de pagamento ao abrir modal
  const resetPaymentForm = () => {
    setPaymentFormData({
      forma_pagamento: 'PIX',
      valor: '',
      conta_id: '',
      observacao: '',
      bandeira: '',
      parcelas: '1',
      nsu: ''
    });
  };

  const handleUpload = async (orderId, files) => {
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });
      
      await axios.post(`${API}/orders/${orderId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Arquivos anexados!');
      setUploadingOrderId(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao anexar arquivos');
    }
  };

  const handlePrint = async (orderId) => {
    try {
      // Buscar dados do pedido para montar o nome do arquivo
      const order = orders.find(o => o.id === orderId);
      const numeroPedido = order?.numero_pedido || `PED-${orderId.slice(0, 8)}`;
      const clienteNome = order?.cliente_nome || 'Cliente';
      // Limpar nome do cliente para nome de arquivo válido
      const clienteNomeFile = clienteNome.replace(/[/\\:*?"<>|]/g, '-');
      const filename = `${numeroPedido} - ${clienteNomeFile}.pdf`;
      
      const response = await axios.get(`${API}/orders/${orderId}/print`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erro ao imprimir pedido');
    }
  };

  // Visualizar PDF em nova aba
  const handleViewPDF = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/print`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erro ao visualizar PDF');
    }
  };

  const filteredOrders = useMemo(() => {
    // Quando há busca ativa, o backend já filtrou — não refiltrar por texto
    // (evita descartar matches feitos por CPF/CNPJ ou código de produto que
    //  o backend resolveu via lookup em customers/products).
    const hasBackendSearch = !!(searchTerm && searchTerm.trim());
    return orders.filter(o => {
      let matchText = true;
      if (!hasBackendSearch) {
        matchText = true; // sem busca, aceita tudo
      }
      // Filtro de ano
      const orderYear = o.data_pedido?.substring(0, 4);
      const matchYear = !filterYear || orderYear === filterYear;
      
      // Filtro de mês
      const orderMonth = o.data_pedido?.substring(5, 7);
      const matchMonth = !filterMonth || orderMonth === filterMonth;
      
      return matchText && matchYear && matchMonth;
    });
  }, [orders, searchTerm, filterYear, filterMonth]);

  // Draggable Order Card Component
  const DraggableOrderCard = ({ order }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
      id: order.id,
    });
    const [showUpload, setShowUpload] = useState(false);

    const style = transform ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      zIndex: isDragging ? 1000 : 1,
    } : undefined;

    const isFinalizado = order.status === 'Finalizado';
    const isEntregue = order.status === 'Entregue';
    
    // Verificar se está atrasado (passou da data de entrega e não está finalizado nem entregue)
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    // Corrigir problema de timezone ao comparar datas
    const datePart = order.data_entrega_prevista.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const dataEntrega = new Date(year, month - 1, day);
    const isPronto = order.status === 'Pronto';
    // Não mostrar atraso para pedidos Pronto, Entregue ou Finalizado
    const isAtrasado = !isFinalizado && !isEntregue && !isPronto && dataEntrega < hoje;
    
    // Verificar se pagamento está pendente
    const isPagamentoPendente = order.status_financeiro !== 'Pago Total';

    const handleCardClick = (e) => {
      // Don't open edit if dragging
      if (isDragging) return;
      handleEdit(order);
    };

    // Definir classes do card baseado no estado
    let cardClass = 'transition-all duration-200 hover:border-primary hover:shadow-md';
    if (isDragging) {
      cardClass += ' opacity-50 scale-105 shadow-xl ring-2 ring-primary';
    }
    if (isFinalizado) {
      cardClass += ' bg-green-500/20 border-green-500';
    } else if (isEntregue) {
      cardClass += ' bg-orange-500/10 border-orange-500';
    } else if (isAtrasado) {
      cardClass += ' bg-destructive/20 border-destructive';
    }

    return (
      <Card 
        ref={setNodeRef}
        style={style}
        className={`${cardClass} overflow-hidden`}
      >
        <CardContent className="p-3 space-y-2">
          <div className="flex justify-between items-start">
            <div 
              className="flex items-center gap-2 cursor-grab active:cursor-grabbing"
              {...attributes}
              {...listeners}
            >
              <GripVertical className={`h-5 w-5 ${isAtrasado ? 'text-destructive' : 'text-muted-foreground'}`} />
              <p className={`font-semibold text-lg ${isFinalizado ? 'text-green-500' : isAtrasado ? 'text-destructive' : ''}`}>
                {order.numero_pedido || `#${order.id.slice(0, 8)}`}
              </p>
              {order.nfe_faturada && (
                <Badge className="bg-green-600/20 text-green-500 text-xs px-1.5 py-0.5">
                  NF-e {order.nfe_numero || '✓'}
                </Badge>
              )}
              {isAtrasado && <AlertTriangle className="h-4 w-4 text-destructive" />}
            </div>
            <div className="flex items-center gap-1">
              <Badge 
                variant="outline" 
                className={`text-sm px-2 py-0.5 ${
                  isEntregue && isPagamentoPendente 
                    ? 'bg-red-500/20 text-red-500 border-red-500' 
                    : FINANCEIRO_COLORS[order.status_financeiro]
                }`}
              >
                {order.status_financeiro === 'Pago Total' ? '✓ Pago' : order.status_financeiro}
              </Badge>
              {/* Botão de menu mobile para mover */}
              <button 
                className="md:hidden p-1 hover:bg-muted rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMobileMenu(showMobileMenu === order.id ? null : order.id);
                }}
              >
                <ChevronRight className={`h-4 w-4 transition-transform ${showMobileMenu === order.id ? 'rotate-90' : ''}`} />
              </button>
            </div>
          </div>
          
          {/* Menu de ações mobile para mover pedido */}
          {showMobileMenu === order.id && (
            <div className="md:hidden mb-2 p-2 bg-muted/50 rounded-lg space-y-1">
              <p className="text-xs text-muted-foreground mb-2">Mover para:</p>
              <div className="flex flex-wrap gap-1">
                {KANBAN_COLUMNS.map((targetStatus) => {
                  if (order.status === targetStatus) return null;
                  return (
                    <button
                      key={targetStatus}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveOrder(order, targetStatus);
                      }}
                      className={`text-xs px-2 py-1 rounded text-white ${STATUS_COLORS[targetStatus]?.replace('text-', 'bg-').replace('/10', '').replace('/20', '') || 'bg-gray-500'}`}
                      style={{
                        backgroundColor: targetStatus === 'Aguardando' ? '#eab308' : 
                                        targetStatus === 'Em Produção' ? '#3b82f6' : 
                                        targetStatus === 'Pronto' ? '#a855f7' : 
                                        targetStatus === 'Entregue Parcial' ? '#d97706' :
                                        targetStatus === 'Entregue' ? '#f97316' : 
                                        targetStatus === 'Finalizado' ? '#22c55e' : '#6b7280'
                      }}
                    >
                      {targetStatus}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          <div 
            className="cursor-pointer space-y-2"
            onClick={handleCardClick}
          >
            <p className={`font-medium text-base ${isFinalizado ? 'text-green-500/80' : isAtrasado ? 'text-destructive/80' : ''}`}>
              {order.cliente_nome}
            </p>
            {order.cliente_telefone && (
              <div className="flex items-center gap-2">
                <a 
                  href={getWhatsAppLink(order.cliente_telefone)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-green-500"
                  onClick={(e) => e.stopPropagation()}
                  title="Abrir WhatsApp"
                >
                  📱 {order.cliente_telefone}
                </a>
                <a 
                  href={getWhatsAppLink(order.cliente_telefone, getMensagemPedidoPronto(order.cliente_nome))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-500 hover:text-green-600"
                  onClick={(e) => e.stopPropagation()}
                  title="Pedido pronto - enviar mensagem"
                >
                  <Package className="h-4 w-4" />
                </a>
                <a 
                  href={getWhatsAppLink(order.cliente_telefone, getMensagemEntregaACaminho(order.cliente_nome))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600"
                  onClick={(e) => e.stopPropagation()}
                  title="Entrega a caminho - enviar mensagem"
                >
                  <Truck className="h-4 w-4" />
                </a>
                {order.cliente_endereco && (
                  <a 
                    href={getGoogleMapsLink(order.cliente_endereco)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-500 hover:text-red-600"
                    onClick={(e) => e.stopPropagation()}
                    title={`Abrir no Google Maps: ${order.cliente_endereco}`}
                  >
                    <MapPin className="h-4 w-4" />
                  </a>
                )}
              </div>
            )}
            {!order.cliente_telefone && order.cliente_endereco && (
              <a 
                href={getGoogleMapsLink(order.cliente_endereco)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-red-500"
                onClick={(e) => e.stopPropagation()}
                title="Abrir no Google Maps"
              >
                <MapPin className="h-4 w-4" />
                <span className="truncate max-w-[150px]">{order.cliente_endereco}</span>
              </a>
            )}
            <div className="flex justify-between items-start">
              <p className={`font-mono font-bold text-lg ${isFinalizado ? 'text-green-500' : isAtrasado ? 'text-destructive' : 'text-primary'}`}>
                R$ {(order.total_com_desconto || order.total_venda).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <div className={`text-right leading-tight ${isFinalizado ? 'text-green-500/70' : isAtrasado ? 'text-destructive/70' : 'text-muted-foreground'}`}>
                {order.data_pedido && (
                  <p className="text-xs" data-testid={`order-card-data-pedido-${order.id}`}>
                    <span className="opacity-70">Pedido:</span> {formatDateDisplay(order.data_pedido)}
                  </p>
                )}
                {order.data_entrega_prevista && (
                  <p className="text-xs" data-testid={`order-card-data-entrega-${order.id}`}>
                    <span className="opacity-70">Entrega:</span> {formatDateDisplay(order.data_entrega_prevista)}
                  </p>
                )}
              </div>
            </div>
            {order.desconto_valor > 0 && (
              <p className="text-sm text-destructive">
                Desc: -{order.desconto_tipo === 'percentual' ? `${order.desconto_valor}%` : `R$ ${order.desconto_valor}`}
              </p>
            )}
            {/* Barra de progresso de pagamento */}
            {(() => {
              const total = order.total_com_desconto || order.total_venda || 0;
              const pago = order.total_pago || 0;
              const percentual = total > 0 ? Math.min((pago / total) * 100, 100) : 0;
              const isPagoTotal = percentual >= 100;
              return (
                <div className="mt-2 pt-2 border-t border-muted">
                  <div className="flex justify-between text-sm mb-1">
                    <span className={isPagoTotal ? 'text-green-500 font-medium' : isFinalizado ? 'text-green-500/70' : isAtrasado ? 'text-destructive/70' : 'text-muted-foreground'}>
                      {percentual.toFixed(0)}%
                    </span>
                    <span className={`font-mono ${isPagoTotal ? 'text-green-500' : isFinalizado ? 'text-green-500/70' : isAtrasado ? 'text-destructive/70' : 'text-muted-foreground'}`}>
                      R$ {pago.toLocaleString('pt-BR', { minimumFractionDigits: 0 })} / R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        isPagoTotal ? 'bg-green-500' : percentual > 0 ? 'bg-yellow-500' : 'bg-muted-foreground/30'
                      }`}
                      style={{ width: `${percentual}%` }}
                    />
                  </div>
                </div>
              );
            })()}
            {/* Margem de Lucro */}
            {order.margem_lucro !== undefined && order.total_venda > 0 && (
              <div className="flex items-center justify-between text-xs mt-1 px-1">
                <span className="text-muted-foreground">Lucro:</span>
                <div className="flex items-center gap-2">
                  <span className={`font-mono ${order.margem_lucro > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    R$ {order.margem_lucro.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                  </span>
                  <Badge 
                    variant="outline" 
                    className={`text-xs px-1.5 py-0 ${
                      ((order.margem_lucro / order.total_venda) * 100) >= 30 
                        ? 'bg-green-500/20 text-green-500 border-green-500/50' 
                        : ((order.margem_lucro / order.total_venda) * 100) >= 15 
                          ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50' 
                          : 'bg-red-500/20 text-red-500 border-red-500/50'
                    }`}
                  >
                    {((order.margem_lucro / order.total_venda) * 100).toFixed(0)}%
                  </Badge>
                </div>
              </div>
            )}
            {isAtrasado && (
              <p className="text-sm text-destructive font-medium mt-1">
                ⚠ Atrasado
              </p>
            )}
          </div>
          {/* Ações rápidas do card */}
          <div className="pt-3 border-t border-border/50 space-y-2">
            <div className="flex flex-wrap gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={(e) => { e.stopPropagation(); setViewingOrder(order); }}
                title="Ver detalhes"
              >
                <Eye className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={handleCardClick}
                title="Editar"
              >
                <Edit className="h-3.5 w-3.5" />
              </Button>
              {/* Botões de integração Omie */}
              {omieStatus?.ativo && (
                <>
                  {!order.enviado_omie ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-1.5 text-xs bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/30"
                      onClick={(e) => { e.stopPropagation(); handleSendToOmie(order.id); }}
                      disabled={sendingToOmie === order.id}
                      title="Enviar para Omie"
                    >
                      {sendingToOmie === order.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <Cloud className="h-3 w-3 mr-0.5" />
                          Omie
                        </>
                      )}
                    </Button>
                  ) : !order.faturado_omie ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-1.5 text-xs bg-green-500/10 hover:bg-green-500/20 border-green-500/30"
                      onClick={(e) => { e.stopPropagation(); handleFaturarOmie(order.id); }}
                      disabled={faturandoOmie === order.id}
                      title="Faturar no Omie (gerar NF-e)"
                    >
                      {faturandoOmie === order.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="h-3 w-3 mr-0.5" />
                          Faturar
                        </>
                      )}
                    </Button>
                  ) : (
                    <Badge className="h-7 px-1.5 text-xs bg-green-600/20 text-green-500 border-green-500/30 flex items-center gap-0.5">
                      <CheckCircle className="h-3 w-3" />
                      NF-e {order.numero_nfe_omie || ''}
                    </Badge>
                  )}
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={(e) => { e.stopPropagation(); handleViewPDF(order.id); }}
                title="Visualizar PDF"
              >
                <FileText className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={(e) => { e.stopPropagation(); handlePrint(order.id); }}
                title="Baixar PDF"
              >
                <Printer className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={(e) => { e.stopPropagation(); setShowUpload(!showUpload); }}
                title="Anexar"
              >
                <Paperclip className="h-3.5 w-3.5" />
              </Button>
              {order.anexos && order.anexos.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-1.5 text-xs bg-primary/10 hover:bg-primary/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewingAttachments(order);
                  }}
                  title="Ver anexos"
                >
                  <FileDown className="h-3.5 w-3.5 mr-0.5" />
                  {order.anexos.length}
                </Button>
              )}
              {/* Botão Criar/Ver OS */}
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-1.5 text-xs bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/30 text-blue-600"
                onClick={(e) => { e.stopPropagation(); handleAbrirModalOS(order); }}
                title={order.itens_com_os?.length > 0 ? "Criar nova Ordem de Serviço" : "Criar Ordem de Serviço"}
              >
                <ClipboardList className="h-3 w-3 mr-0.5" />
                {order.itens_com_os?.length > 0 ? '+OS' : 'OS'}
              </Button>
              {order.ordem_servico_id && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-1.5 text-xs bg-green-500/10 hover:bg-green-500/20 border-green-500/30 text-green-600"
                  onClick={(e) => { e.stopPropagation(); window.location.href = '/ordens-servico'; }}
                  title="Ver Ordens de Serviço"
                >
                  <Eye className="h-3 w-3 mr-0.5" />
                  OS
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                onClick={(e) => { e.stopPropagation(); handleDelete(order.id); }}
                title="Excluir"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          {showUpload && (
            <div className="pt-2">
              <Input
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={(e) => { handleUpload(order.id, e.target.files); setShowUpload(false); }}
                className="text-xs"
              />
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Droppable Column Component with visual feedback
  const DroppableColumn = ({ status, children }) => {
    const { setNodeRef, isOver, active } = useDroppable({
      id: status,
    });

    // Verificar se está arrastando para esta coluna
    const isDraggingOver = isOver && active;

    return (
      <div 
        ref={setNodeRef}
        className={`space-y-2 min-h-[200px] p-3 rounded-lg transition-all duration-300 ${
          isDraggingOver 
            ? 'bg-primary/30 ring-2 ring-primary ring-offset-2 shadow-lg scale-[1.01]' 
            : 'bg-muted/20 hover:bg-muted/40'
        }`}
        style={{
          border: isDraggingOver ? '2px dashed hsl(var(--primary))' : '2px dashed transparent',
          minHeight: '250px'
        }}
      >
        {isDraggingOver && (
          <div className="text-center py-2 text-primary font-medium text-sm animate-pulse">
            Solte aqui para mover
          </div>
        )}
        {children}
      </div>
    );
  };

  // Get active order for drag overlay
  const activeOrder = activeId ? orders.find(o => o.id === activeId) : null;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <h1 className="text-3xl font-bold font-manrope tracking-tight">Pedidos e Orçamentos</h1>
            <p className="text-muted-foreground text-sm">Gerencie pedidos de produção e orçamentos</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
          <TabsList className="grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="pedidos" className="flex items-center gap-2" data-testid="tab-pedidos">
              <ShoppingCart className="h-4 w-4" />
              Pedidos
            </TabsTrigger>
            <TabsTrigger value="orcamentos" className="flex items-center gap-2" data-testid="tab-orcamentos">
              <FileText className="h-4 w-4" />
              Orçamentos
            </TabsTrigger>
            <TabsTrigger value="projetados" className="flex items-center gap-2" data-testid="tab-projetados">
              <Package className="h-4 w-4" />
              Projetados
            </TabsTrigger>
            <TabsTrigger value="calculadoras" className="flex items-center gap-2" data-testid="tab-calculadoras">
              <Package className="h-4 w-4" />
              Calculadoras
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pedidos" className="space-y-3">
            {/* Conteúdo de Pedidos */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <NewOrderModal
                isOpen={dialogOpen}
                onOpenChange={(open) => {
                  setDialogOpen(open);
                  if (!open) setEditingOrder(null);
                }}
                editingOrder={editingOrder}
                products={products}
                customers={customers}
                bankAccounts={bankAccounts}
                productCategories={productCategories}
                proximoNumeroPedido={proximoNumeroPedido}
                onSubmitSuccess={fetchData}
                triggerButton={
                  <Button className="shadow-[0_0_10px_rgba(52,211,153,0.2)]">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Pedido
                  </Button>
                }
              />
        </div>

        {/* Filtro Gráfico e View Toggle */}
        <div className="flex gap-2 flex-wrap">
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('kanban')}
            >
              <LayoutGrid className="h-4 w-4 mr-2" />
              Kanban
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4 mr-2" />
              Lista
            </Button>
          </div>
        </div>

        {/* Pesquisa e Filtros */}
        <Card className="border-border">
          <CardContent className="py-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cliente, nº do pedido, CPF, telefone ou produto"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-32"
                  data-testid="orders-search-input"
                />
                {/* Badge de escopo da busca */}
                <span
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-medium px-2 py-0.5 rounded border text-muted-foreground bg-background pointer-events-none"
                  data-testid="orders-search-scope-badge"
                  title={finalizadosLoaded ? 'A busca inclui pedidos finalizados' : 'A busca cobre somente pedidos ativos'}
                >
                  {finalizadosLoaded ? 'ativos + finalizados' : 'ativos'}
                </span>
              </div>
              
              {/* Filtro de período (últimos N meses) */}
              <Select value={filterMonths.toString()} onValueChange={(v) => { setFilterMonths(parseInt(v)); fetchData(1, false); }}>
                <SelectTrigger className="w-auto min-w-[140px]">
                  <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Último mês</SelectItem>
                  <SelectItem value="3">Últimos 3 meses</SelectItem>
                  <SelectItem value="6">Últimos 6 meses</SelectItem>
                  <SelectItem value="12">Último ano</SelectItem>
                  <SelectItem value="0">Todos</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="w-auto min-w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2026, 2025, 2024, 2023].map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterMonth || 'all'} onValueChange={(v) => setFilterMonth(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-auto min-w-[130px]">
                  <Filter className="h-4 w-4 mr-2 flex-shrink-0" />
                  <SelectValue placeholder="Mês" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos meses</SelectItem>
                  {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(m => (
                    <SelectItem key={m} value={m}>
                      {new Date(2024, parseInt(m) - 1).toLocaleDateString('pt-BR', { month: 'long' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Indicador de paginação */}
            <div className="flex items-center justify-between mt-2 text-sm text-muted-foreground">
              <span>
                Exibindo {orders.length} de {pagination.total} pedidos
                {filterMonths > 0 && ` (últimos ${filterMonths} ${filterMonths === 1 ? 'mês' : 'meses'})`}
              </span>
              {pagination.page < pagination.totalPages && (
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={loadMoreOrders}
                  disabled={loadingMore}
                  className="text-primary"
                >
                  {loadingMore ? (
                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Carregando...</>
                  ) : (
                    <>Carregar mais ({pagination.total - orders.length} restantes)</>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Aviso contextual: busca em ativos sem resultado + sugestão para expandir escopo */}
        {searchTerm && searchTerm.trim() && !loading && orders.length === 0 && !finalizadosLoaded && (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 text-amber-900 dark:text-amber-200 px-4 py-3 flex items-center justify-between gap-3"
            data-testid="orders-search-hint-finalizados"
          >
            <div className="text-sm">
              Nenhum pedido ativo encontrado com <strong>"{searchTerm}"</strong>. Clique em <em>Carregar Finalizados</em> para buscar também no histórico.
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => loadFinalizados(1)}
              disabled={finalizadosLoading}
              data-testid="btn-search-expand-finalizados"
              className="shrink-0"
            >
              {finalizadosLoading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Carregando…</> : <>Carregar Finalizados</>}
            </Button>
          </div>
        )}

        {/* Kanban View */}
        {viewMode === 'kanban' && (
          <DndContext
            sensors={sensors}
            collisionDetection={columnOnlyCollision}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 overflow-x-auto">
              {KANBAN_COLUMNS.map(status => {
                const columnOrders = filteredOrders.filter(o => o.status === status);
                const isActiveColumn = activeId && orders.find(o => o.id === activeId)?.status === status;
                const isFinalizadoCol = status === 'Finalizado';
                const showFinalizadosPlaceholder = isFinalizadoCol && !finalizadosLoaded;

                return (
                  <div key={status} className="space-y-3 min-w-[200px]">
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${
                      isFinalizadoCol
                        ? 'bg-green-500/20' 
                        : isActiveColumn 
                          ? 'bg-primary/10 ring-1 ring-primary/50' 
                          : 'bg-muted hover:bg-muted/80'
                    }`}>
                      <h3 className={`font-semibold text-sm ${isFinalizadoCol ? 'text-green-500' : ''}`}>
                        {status}
                      </h3>
                      <Badge variant={isFinalizadoCol ? 'default' : 'secondary'} className="transition-all">
                        {finalizadosLoaded || !isFinalizadoCol ? columnOrders.length : (finalizadosTotal || '···')}
                      </Badge>
                    </div>
                    <DroppableColumn status={status}>
                      {showFinalizadosPlaceholder ? (
                        <Button
                          variant="outline"
                          className={`w-full border-dashed ${searchTerm && searchTerm.trim() ? 'border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/20 animate-pulse' : ''}`}
                          disabled={finalizadosLoading}
                          onClick={() => loadFinalizados(1)}
                          data-testid="btn-carregar-finalizados"
                        >
                          {finalizadosLoading ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Carregando…</>
                          ) : searchTerm && searchTerm.trim() ? (
                            <>Buscar também em finalizados</>
                          ) : (
                            <>Carregar finalizados</>
                          )}
                        </Button>
                      ) : (
                        <>
                          {columnOrders
                            .sort((a, b) => {
                              // Ordenar pela data em que o pedido foi movido para esta coluna
                              const getLastStatusDate = (order) => {
                                if (order.status_moved_at) return new Date(order.status_moved_at);
                                const historico = order.historico || [];
                                for (let i = historico.length - 1; i >= 0; i--) {
                                  if (historico[i].status === order.status) {
                                    return new Date(historico[i].data);
                                  }
                                }
                                return new Date(order.createdAt);
                              };
                              return getLastStatusDate(b) - getLastStatusDate(a);
                            })
                            .map(order => (
                              <DraggableOrderCard key={order.id} order={order} />
                            ))}
                          {isFinalizadoCol && finalizadosLoaded && finalizadosTotal > columnOrders.length && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full"
                              disabled={finalizadosLoading}
                              onClick={() => loadFinalizados(finalizadosPage + 1)}
                              data-testid="btn-mais-finalizados"
                            >
                              {finalizadosLoading ? 'Carregando…' : `Carregar mais (${finalizadosTotal - columnOrders.length} restantes)`}
                            </Button>
                          )}
                        </>
                      )}
                    </DroppableColumn>
                  </div>
                );
              })}
            </div>
            <DragOverlay>
              {activeOrder && (
                <Card className="cursor-grabbing shadow-lg border-primary">
                  <CardContent className="p-3 space-y-2">
                    <p className="font-medium text-sm">
                      {activeOrder.numero_pedido || `#${activeOrder.id.slice(0, 8)}`}
                    </p>
                    <p className="text-xs text-muted-foreground">{activeOrder.cliente_nome}</p>
                    <p className="font-mono font-bold text-primary text-sm">
                      R$ {(activeOrder.total_com_desconto || activeOrder.total_venda).toFixed(2)}
                    </p>
                  </CardContent>
                </Card>
              )}
            </DragOverlay>
          </DndContext>
        )}

        {/* Modal de Visualização do Pedido */}
        <Dialog open={!!viewingOrder} onOpenChange={(open) => !open && setViewingOrder(null)}>
          <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-3 sm:p-8 pb-[1000px] sm:pb-8 rounded-2xl sm:rounded-lg">
            {viewingOrder && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 text-xl sm:text-2xl">
                    <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                    {viewingOrder.numero_pedido || `#${viewingOrder.id.slice(0, 8)}`}
                    {viewingOrder.nfe_faturada && (
                      <Badge className="bg-green-600/20 text-green-500 text-sm">
                        NF-e {viewingOrder.nfe_numero || '✓'}
                      </Badge>
                    )}
                  </DialogTitle>
                  <DialogDescription className="text-base sm:text-lg">
                    {viewingOrder.cliente_nome} • {formatDateDisplay(viewingOrder.data_pedido)}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                  {/* Status e Financeiro */}
                  <div className="flex gap-3 flex-wrap">
                    <Badge variant="outline" className={`text-sm px-3 py-1 ${STATUS_COLORS[viewingOrder.status]}`}>
                      {viewingOrder.status}
                    </Badge>
                    <Badge variant="outline" className={`text-sm px-3 py-1 ${FINANCEIRO_COLORS[viewingOrder.status_financeiro]}`}>
                      {viewingOrder.status_financeiro}
                    </Badge>
                  </div>

                  {/* Produtos */}
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2 text-base sm:text-lg">
                      <Package className="h-5 w-5" />
                      Produtos
                      {viewingOrder.itens_com_os?.length > 0 && (
                        <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30 text-xs">
                          <ClipboardList className="h-3 w-3 mr-1" />
                          {viewingOrder.itens_com_os.length} com OS
                        </Badge>
                      )}
                    </h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full caption-bottom">
                        <thead className="[&_tr]:border-b">
                          <tr className="bg-muted/50 border-b">
                            <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground whitespace-nowrap text-sm sm:text-base">Produto</th>
                            <th className="h-12 px-4 text-center align-middle font-medium text-muted-foreground whitespace-nowrap text-sm sm:text-base w-20">Qtd</th>
                            <th className="h-12 px-4 text-center align-middle font-medium text-muted-foreground whitespace-nowrap text-sm sm:text-base w-16">OS</th>
                            <th className="h-12 px-4 text-right align-middle font-medium text-muted-foreground whitespace-nowrap text-sm sm:text-base">Valor</th>
                          </tr>
                        </thead>
                        <tbody className="[&_tr:last-child]:border-0">
                        {viewingOrder.produtos.map((prod, idx) => {
                          // Verificar se este produto está na OS
                          const itemNaOS = viewingOrder.itens_com_os?.find(
                            item => item.produto_id === prod.produto_id || item.produto_nome === prod.nome
                          );
                          // Variações selecionadas
                          const variacoes = prod.variacoes_selecionadas || [];
                          return (
                            <tr key={`produto-${prod.produto_id || prod.nome}-${idx}`} className={`border-b transition-colors hover:bg-muted/50 ${itemNaOS ? 'bg-blue-500/5' : ''}`}>
                              <td className="p-4 align-middle text-sm sm:text-base">
                                <div>
                                  <span>{prod.nome}</span>
                                  {variacoes.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {variacoes.map((v, vIdx) => (
                                        <Badge key={vIdx} variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30">
                                          {v.nome}
                                          {v.valor_adicional > 0 && ` (+R$ ${v.valor_adicional.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                  {prod.observacoes && (
                                    <p className="text-xs text-muted-foreground mt-1">OBS: {prod.observacoes}</p>
                                  )}
                                </div>
                              </td>
                              <td className="p-4 align-middle text-center text-sm sm:text-base font-medium">{prod.quantidade}</td>
                              <td className="p-4 align-middle text-center">
                                {itemNaOS ? (
                                  <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30 text-xs">
                                    OS-{String(itemNaOS.numero_os).padStart(4, '0')}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground text-xs">-</span>
                                )}
                              </td>
                              <td className="p-4 align-middle text-right font-mono text-sm sm:text-base whitespace-nowrap text-primary font-bold">
                                R$ {((prod.preco_unitario || 0) * prod.quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          );
                        })}
                        {/* Subtotal */}
                        <tr className="bg-muted/20 border-b">
                          <td colSpan={3} className="p-4 align-middle text-right text-sm sm:text-base font-medium">Subtotal:</td>
                          <td className="p-4 align-middle text-right font-mono text-sm sm:text-base whitespace-nowrap font-medium">
                            R$ {(viewingOrder.total_venda || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                        {/* Desconto */}
                        {viewingOrder.desconto_valor > 0 && (
                          <tr className="bg-red-500/10 border-b">
                            <td colSpan={3} className="p-4 align-middle text-right text-red-500 text-sm sm:text-base font-medium">
                              Desconto ({viewingOrder.desconto_tipo === 'percentual' ? `${viewingOrder.desconto_valor}%` : 'Fixo'}):
                            </td>
                            <td className="p-4 align-middle text-right font-mono text-red-500 font-bold text-sm sm:text-base whitespace-nowrap">
                              -R$ {viewingOrder.desconto_tipo === 'percentual' 
                                ? ((viewingOrder.total_venda * viewingOrder.desconto_valor / 100) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : (viewingOrder.desconto_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        )}
                        {/* Total */}
                        <tr className="bg-muted/30 font-bold border-t-2">
                          <td colSpan={3} className="p-4 align-middle text-right text-base sm:text-lg">Total:</td>
                          <td className="p-4 align-middle text-right font-mono text-primary text-lg sm:text-xl whitespace-nowrap">
                            R$ {(viewingOrder.total_com_desconto || viewingOrder.total_venda).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    </div>
                  </div>

                  {/* Pagamentos do Pedido */}
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2 text-base sm:text-lg">
                      <CreditCard className="h-5 w-5" />
                      Pagamentos
                    </h4>
                    {viewingOrder.pagamentos && viewingOrder.pagamentos.length > 0 ? (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full caption-bottom">
                          <thead className="[&_tr]:border-b">
                            <tr className="bg-muted/50 border-b">
                              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sm:text-base">Data</th>
                              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sm:text-base">Forma</th>
                              <th className="h-12 px-4 text-right align-middle font-medium text-muted-foreground text-sm sm:text-base">Valor</th>
                              <th className="h-12 px-4 text-center align-middle font-medium text-muted-foreground text-sm sm:text-base">Recibo</th>
                            </tr>
                          </thead>
                          <tbody className="[&_tr:last-child]:border-0">
                            {viewingOrder.pagamentos.map((pag, idx) => (
                              <tr key={pag.id || idx} className="border-b transition-colors hover:bg-muted/50">
                                <td className="p-4 align-middle font-mono text-sm sm:text-base whitespace-nowrap">
                                  {pag.data_pagamento 
                                    ? formatDateDisplay(pag.data_pagamento)
                                    : '-'}
                                </td>
                                <td className="p-4 align-middle">
                                  <Badge className={`text-sm px-3 py-1 ${
                                    pag.forma_pagamento === 'Crédito' ? 'bg-purple-500/20 text-purple-500' :
                                    pag.forma_pagamento === 'Débito' ? 'bg-blue-500/20 text-blue-500' :
                                    pag.forma_pagamento === 'PIX' ? 'bg-green-500/20 text-green-500' :
                                    'bg-gray-500/20 text-gray-500'
                                  }`} variant="secondary">
                                    {pag.forma_pagamento}
                                  </Badge>
                                  {pag.bandeira && (
                                    <span className="ml-2 text-sm text-muted-foreground">{pag.bandeira}</span>
                                  )}
                                  {pag.parcelas > 1 && (
                                    <span className="ml-2 text-sm font-mono font-medium">{pag.parcelas}x</span>
                                  )}
                                </td>
                                <td className="p-4 align-middle text-right font-mono font-bold text-green-500 text-base sm:text-lg whitespace-nowrap">
                                  R$ {(pag.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="p-4 align-middle text-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 px-3 text-xs"
                                    onClick={async () => {
                                      try {
                                        const response = await axios.get(`${API}/orders/${viewingOrder.id}/recibo/${idx}`, {
                                          responseType: 'blob'
                                        });
                                        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', `Recibo_${viewingOrder.numero_pedido}_${idx + 1}.pdf`);
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                        window.URL.revokeObjectURL(url);
                                        toast.success('Recibo gerado com sucesso!');
                                      } catch (error) {
                                        console.error('Erro ao gerar recibo:', error);
                                        toast.error('Erro ao gerar recibo');
                                      }
                                    }}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    Gerar
                                  </Button>
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-muted/30 font-bold border-t-2">
                              <td colSpan={3} className="p-4 align-middle text-right text-base sm:text-lg">
                                Total:
                              </td>
                              <td className="p-4 align-middle text-right font-mono text-green-500 text-lg sm:text-xl whitespace-nowrap">
                                R$ {(viewingOrder.total_pago || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="p-4 align-middle text-center">
                                {viewingOrder.pagamentos.length > 1 && (
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className="h-8 px-3 text-xs bg-green-600 hover:bg-green-700"
                                    onClick={async () => {
                                      try {
                                        const response = await axios.get(`${API}/orders/${viewingOrder.id}/recibo-completo`, {
                                          responseType: 'blob'
                                        });
                                        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', `Recibo_Completo_${viewingOrder.numero_pedido}.pdf`);
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                        window.URL.revokeObjectURL(url);
                                        toast.success('Recibo completo gerado!');
                                      } catch (error) {
                                        console.error('Erro ao gerar recibo:', error);
                                        toast.error('Erro ao gerar recibo');
                                      }
                                    }}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    Todos
                                  </Button>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-base text-muted-foreground text-center py-6 border rounded-lg">
                        Nenhum pagamento registrado
                      </p>
                    )}
                  </div>

                  {/* Entregas */}
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2 text-base sm:text-lg">
                      <Truck className="h-5 w-5" />
                      Entregas
                    </h4>
                    <EntregasTab 
                      order={viewingOrder} 
                      onUpdate={() => {
                        fetchOrders();
                      }} 
                    />
                  </div>

                  {/* Observações */}
                  {viewingOrder.observacoes && (
                    <div>
                      <h4 className="font-semibold mb-3 text-base sm:text-lg">Observações</h4>
                      <p className="text-sm sm:text-base text-muted-foreground bg-muted/30 p-4 rounded-lg">
                        {viewingOrder.observacoes}
                      </p>
                    </div>
                  )}

                  {/* Botões de Ação */}
                  <div className="flex flex-wrap gap-2 sm:gap-3 pt-6 border-t">
                    <Button size="sm" className="flex-1 sm:flex-none" variant="outline" onClick={() => { setViewingOrder(null); handleEdit(viewingOrder); }}>
                      <Edit className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    <Button size="sm" className="flex-1 sm:flex-none" variant="outline" onClick={() => handleViewPDF(viewingOrder.id)}>
                      <FileText className="h-4 w-4 mr-1" />
                      Ver PDF
                    </Button>
                    <Button size="sm" className="flex-1 sm:flex-none" variant="outline" onClick={() => handlePrint(viewingOrder.id)}>
                      <Printer className="h-4 w-4 mr-1" />
                      Baixar
                    </Button>
                    {/* Botão Criar OS */}
                    <Button 
                      size="sm" 
                      className="flex-1 sm:flex-none bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/30 text-blue-600" 
                      variant="outline"
                      onClick={() => { setViewingOrder(null); handleAbrirModalOS(viewingOrder); }}
                    >
                      <ClipboardList className="h-4 w-4 mr-1" />
                      {viewingOrder.itens_com_os?.length > 0 ? 'Nova OS' : 'Criar OS'}
                    </Button>
                    {/* Ver OS */}
                    {viewingOrder.ordem_servico_id && (
                      <Button 
                        size="sm" 
                        className="flex-1 sm:flex-none bg-green-500/10 hover:bg-green-500/20 border-green-500/30 text-green-600" 
                        variant="outline"
                        onClick={() => window.location.href = '/ordens-servico'}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Ver OS
                      </Button>
                    )}
                    {/* Botões de integração Omie */}
                    {omieStatus?.ativo && (
                      <>
                        {!viewingOrder.enviado_omie ? (
                          <Button
                            size="sm"
                            className="flex-1 sm:flex-none bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/30"
                            variant="outline"
                            onClick={() => { handleSendToOmie(viewingOrder.id); }}
                            disabled={sendingToOmie === viewingOrder.id}
                          >
                            {sendingToOmie === viewingOrder.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <Cloud className="h-4 w-4 mr-1" />
                            )}
                            Omie
                          </Button>
                        ) : !viewingOrder.faturado_omie ? (
                          <Button
                            size="sm"
                            className="flex-1 sm:flex-none bg-green-500/10 hover:bg-green-500/20 border-green-500/30"
                            variant="outline"
                            onClick={() => { handleFaturarOmie(viewingOrder.id); }}
                            disabled={faturandoOmie === viewingOrder.id}
                          >
                            {faturandoOmie === viewingOrder.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <CheckCircle className="h-4 w-4 mr-1" />
                            )}
                            Faturar
                          </Button>
                        ) : (
                          <Badge className="h-9 px-3 text-sm bg-green-600/20 text-green-500 border-green-500/30 flex items-center gap-1">
                            <CheckCircle className="h-4 w-4" />
                            NF-e {viewingOrder.numero_nfe_omie || '✓'}
                          </Badge>
                        )}
                      </>
                    )}
                    {/* Anexos */}
                    {viewingOrder.anexos && viewingOrder.anexos.length > 0 && (
                      <Button
                        size="sm"
                        className="flex-1 sm:flex-none bg-primary/10 hover:bg-primary/20"
                        variant="outline"
                        onClick={() => { setViewingOrder(null); setViewingAttachments(viewingOrder); }}
                      >
                        <Paperclip className="h-4 w-4 mr-1" />
                        Anexos ({viewingOrder.anexos.length})
                      </Button>
                    )}
                    {/* Excluir */}
                    <Button 
                      size="sm" 
                      className="flex-1 sm:flex-none text-destructive hover:text-destructive" 
                      variant="outline"
                      onClick={() => { setViewingOrder(null); handleDelete(viewingOrder.id); }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Excluir
                    </Button>
                    <Button size="sm" className="w-full sm:w-auto" variant="secondary" onClick={() => setViewingOrder(null)}>
                      Fechar
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Anexos do Pedido */}
        <Dialog open={!!viewingAttachments} onOpenChange={(open) => !open && setViewingAttachments(null)}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Paperclip className="h-5 w-5" />
                Anexos do Pedido {viewingAttachments?.numero_pedido || `#${viewingAttachments?.id?.slice(0, 8)}`}
              </DialogTitle>
              <DialogDescription>
                {viewingAttachments?.anexos?.length || 0} arquivo(s) anexado(s)
              </DialogDescription>
            </DialogHeader>
            {viewingAttachments && (
              <div className="space-y-3">
                {viewingAttachments.anexos && viewingAttachments.anexos.length > 0 ? (
                  viewingAttachments.anexos.map((anexo, idx) => {
                    const filename = typeof anexo === 'string' ? anexo.split('/').pop() : anexo.filepath?.split('/').pop();
                    const displayName = typeof anexo === 'string' ? `Arquivo ${idx + 1}` : (anexo.filename || anexo.original_filename || `Arquivo ${idx + 1}`);
                    const fileType = anexo.tipo || (filename?.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Imagem');
                    const uploadDate = anexo.uploaded_at ? new Date(anexo.uploaded_at).toLocaleDateString('pt-BR') : null;
                    const url = `${API}/uploads/${filename}`;
                    
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded ${fileType === 'PDF' || fileType === 'nfe_pdf' ? 'bg-red-500/20 text-red-500' : 'bg-blue-500/20 text-blue-500'}`}>
                            <FileDown className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{displayName}</p>
                            <div className="flex gap-2 text-xs text-muted-foreground">
                              <span className="uppercase">{fileType === 'nfe_pdf' ? 'NF-e PDF' : fileType}</span>
                              {uploadDate && <span>• {uploadDate}</span>}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => window.open(url, '_blank')}
                          data-testid={`download-attachment-${idx}`}
                        >
                          <FileDown className="h-4 w-4 mr-1" />
                          Baixar
                        </Button>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhum anexo encontrado
                  </p>
                )}
                <div className="pt-4 border-t">
                  <Button variant="outline" className="w-full" onClick={() => setViewingAttachments(null)}>
                    Fechar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Lista de Pedidos (modo lista) */}
        {viewMode === 'list' && (
        <Accordion type="single" collapsible className="space-y-3">
          {filteredOrders
            .sort((a, b) => {
              // Ordenar pela data da última atualização de status (histórico)
              const getLastStatusDate = (order) => {
                const historico = order.historico || [];
                if (historico.length > 0) {
                  return new Date(historico[historico.length - 1].data);
                }
                return new Date(order.createdAt);
              };
              return getLastStatusDate(b) - getLastStatusDate(a);
            })
            .map(order => (
            <AccordionItem key={order.id} value={order.id} className="border-0">
              <Card className="border-border">
                <AccordionTrigger className="hover:no-underline p-0">
                  <CardHeader className="w-full">
                    <div className="flex justify-between items-center w-full">
                      <div className="flex items-center gap-3">
                        <ShoppingCart className="h-5 w-5 text-primary" />
                        <div className="text-left">
                          <CardTitle className="font-manrope text-lg">
                            {order.numero_pedido || `#${order.id.slice(0, 8).toUpperCase()}`}
                          </CardTitle>
                          <p className="text-sm text-muted-foreground">{order.cliente_nome}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateDisplay(order.data_pedido)}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 items-center mr-4">
                        <Badge variant="outline" className={STATUS_COLORS[order.status]}>
                          {order.status}
                        </Badge>
                        <Badge variant="outline" className={FINANCEIRO_COLORS[order.status_financeiro]}>
                          {order.status_financeiro}
                        </Badge>
                        {order.nfe_faturada && (
                          <Badge className="bg-green-600/20 text-green-500">
                            NF-e {order.nfe_numero || '✓'}
                          </Badge>
                        )}
                        <span className="font-mono font-bold text-primary">
                          R$ {order.total_venda.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                </AccordionTrigger>
                <AccordionContent>
                  <CardContent className="space-y-4">
                    {/* WhatsApp Links */}
                    {order.cliente_telefone && (
                      <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                        <a 
                          href={getWhatsAppLink(order.cliente_telefone)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm hover:text-green-500"
                          title="Abrir WhatsApp"
                        >
                          📱 {order.cliente_telefone}
                        </a>
                        <a 
                          href={getWhatsAppLink(order.cliente_telefone, getMensagemPedidoPronto(order.cliente_nome))}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-green-500 hover:text-green-600 bg-green-500/10 px-2 py-1 rounded"
                          title="Pedido pronto - enviar mensagem"
                        >
                          <Package className="h-4 w-4" />
                          Pedido Pronto
                        </a>
                        <a 
                          href={getWhatsAppLink(order.cliente_telefone, getMensagemEntregaACaminho(order.cliente_nome))}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 bg-blue-500/10 px-2 py-1 rounded"
                          title="Entrega a caminho - enviar mensagem"
                        >
                          <Truck className="h-4 w-4" />
                          Entrega a Caminho
                        </a>
                        {order.cliente_endereco && (
                          <a 
                            href={getGoogleMapsLink(order.cliente_endereco)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-red-500 hover:text-red-600 bg-red-500/10 px-2 py-1 rounded"
                            title={`Abrir no Google Maps: ${order.cliente_endereco}`}
                          >
                            <MapPin className="h-4 w-4" />
                            Ver no Mapa
                          </a>
                        )}
                      </div>
                    )}
                    {/* Endereço sem telefone */}
                    {!order.cliente_telefone && order.cliente_endereco && (
                      <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                        <a 
                          href={getGoogleMapsLink(order.cliente_endereco)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600"
                          title="Abrir no Google Maps"
                        >
                          <MapPin className="h-4 w-4" />
                          <span>{order.cliente_endereco}</span>
                        </a>
                      </div>
                    )}
                    {/* Status */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Status do Pedido</Label>
                        <Select value={order.status} onValueChange={(value) => handleUpdateStatus(order.id, value, null)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Status Financeiro</Label>
                        <Select value={order.status_financeiro} onValueChange={(value) => handleUpdateStatus(order.id, null, value)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_FINANCEIRO_OPTIONS.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Data Pedido:</span>
                        <p className="font-medium">{formatDateDisplay(order.data_pedido)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Entrega:</span>
                        <p className="font-medium">{formatDateDisplay(order.data_entrega_prevista)}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-2">Produtos:</p>
                      <div className="space-y-1">
                        {order.produtos.map((prod, idx) => (
                          <div key={idx} className="flex justify-between text-sm bg-muted/50 p-2 rounded">
                            <span>{prod.nome} x {prod.quantidade}</span>
                            <span className="font-mono">R$ {(prod.preco_unitario * prod.quantidade).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Anexos */}
                    {order.anexos && order.anexos.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Anexos ({order.anexos.length}):</p>
                        <div className="flex flex-wrap gap-2">
                          {order.anexos.map((anexo, idx) => {
                            // Build URL with /api prefix for proper routing
                            const filename = typeof anexo === 'string' ? anexo.split('/').pop() : anexo.filepath.split('/').pop();
                            const url = `${API}/uploads/${filename}`;
                            return (
                              <Button
                                key={idx}
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(url, '_blank')}
                              >
                                <FileDown className="h-4 w-4 mr-2" />
                                {typeof anexo === 'string' ? `Arquivo ${idx + 1}` : anexo.filename}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}


                    {/* MÚLTIPLOS PAGAMENTOS */}
                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <Label className="text-lg">Pagamentos Recebidos</Label>
                        <Dialog onOpenChange={(open) => { if (open) resetPaymentForm(); }}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline">
                              <Plus className="h-4 w-4 mr-2" />
                              Adicionar Pagamento
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
                            <DialogHeader>
                              <DialogTitle>Registrar Pagamento</DialogTitle>
                              <DialogDescription>
                                Adicione um pagamento parcial ou total
                              </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={async (e) => {
                              e.preventDefault();
                              await handleSubmitPayment(order.id);
                            }} className="space-y-4">
                              <div className="space-y-2">
                                <Label>Forma de Pagamento</Label>
                                <select 
                                  className="w-full p-2 border rounded bg-background text-base md:text-sm" 
                                  value={paymentFormData.forma_pagamento}
                                  onChange={(e) => setPaymentFormData(prev => ({ ...prev, forma_pagamento: e.target.value }))}
                                >
                                  <option value="PIX">PIX</option>
                                  <option value="Espécie">Espécie</option>
                                  <option value="Débito">Cartão de Débito</option>
                                  <option value="Crédito">Cartão de Crédito</option>
                                  <option value="Boleto">Boleto</option>
                                  <option value="Transferência">Transferência</option>
                                </select>
                              </div>
                              
                              {/* Campos condicionais para cartão */}
                              {(paymentFormData.forma_pagamento === 'Débito' || paymentFormData.forma_pagamento === 'Crédito') && (
                                <>
                                  <div className="space-y-2">
                                    <Label>Bandeira do Cartão <span className="text-red-500">*</span></Label>
                                    <select 
                                      className="w-full p-2 border rounded bg-background text-base md:text-sm" 
                                      value={paymentFormData.bandeira}
                                      onChange={(e) => setPaymentFormData(prev => ({ ...prev, bandeira: e.target.value }))}
                                      required
                                    >
                                      <option value="">Selecione a bandeira...</option>
                                      <option value="visa">Visa</option>
                                      <option value="mastercard">Mastercard</option>
                                      <option value="elo">Elo</option>
                                      <option value="amex">American Express</option>
                                      <option value="hipercard">Hipercard</option>
                                    </select>
                                  </div>
                                  
                                  {paymentFormData.forma_pagamento === 'Crédito' && (
                                    <div className="space-y-2">
                                      <Label>Quantidade de Parcelas</Label>
                                      <select 
                                        className="w-full p-2 border rounded bg-background text-base md:text-sm" 
                                        value={paymentFormData.parcelas}
                                        onChange={(e) => setPaymentFormData(prev => ({ ...prev, parcelas: e.target.value }))}
                                      >
                                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                                          <option key={n} value={n}>{n}x</option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                  
                                  <div className="space-y-2">
                                    <Label>NSU da Transação <span className="text-red-500">*</span></Label>
                                    <Input
                                      placeholder="Número Sequencial Único"
                                      value={paymentFormData.nsu}
                                      onChange={(e) => setPaymentFormData(prev => ({ ...prev, nsu: e.target.value }))}
                                      required
                                    />
                                    <p className="text-xs text-muted-foreground">
                                      O NSU está no comprovante da maquininha
                                    </p>
                                  </div>
                                </>
                              )}
                              
                              <div className="space-y-2">
                                <Label>Valor <span className="text-red-500">*</span></Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={paymentFormData.valor}
                                  onChange={(e) => setPaymentFormData(prev => ({ ...prev, valor: e.target.value }))}
                                  required
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Conta Bancária</Label>
                                <select 
                                  className="w-full p-2 border rounded bg-background text-base md:text-sm"
                                  value={paymentFormData.conta_id}
                                  onChange={(e) => setPaymentFormData(prev => ({ ...prev, conta_id: e.target.value }))}
                                >
                                  <option value="">Selecione a conta...</option>
                                  {bankAccounts.map(acc => (
                                    <option key={acc.id} value={acc.id}>
                                      {acc.nome} {acc.conta_cartao ? '(Cartões)' : ''}
                                    </option>
                                  ))}
                                </select>
                                {(paymentFormData.forma_pagamento === 'Débito' || paymentFormData.forma_pagamento === 'Crédito') && (
                                  <p className="text-xs text-muted-foreground">
                                    Se não selecionar, usará a conta de cartões padrão
                                  </p>
                                )}
                              </div>
                              <div className="space-y-2">
                                <Label>Observação (opcional)</Label>
                                <Input
                                  placeholder="Ex: Entrada do pedido"
                                  value={paymentFormData.observacao}
                                  onChange={(e) => setPaymentFormData(prev => ({ ...prev, observacao: e.target.value }))}
                                />
                              </div>
                              <div className="bg-muted p-3 rounded">
                                <p className="text-sm text-muted-foreground">Total do pedido:</p>
                                <p className="text-lg font-bold">R$ {order.total_venda.toFixed(2)}</p>
                                <p className="text-sm text-muted-foreground mt-2">
                                  Pago até agora: R$ {(order.total_pago || 0).toFixed(2)}
                                </p>
                                <p className="text-sm font-medium">
                                  Restante: R$ {(order.total_venda - (order.total_pago || 0)).toFixed(2)}
                                </p>
                              </div>
                              <Button type="submit" className="w-full">
                                <CreditCard className="h-4 w-4 mr-2" />
                                Registrar Pagamento
                              </Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>

                      {order.pagamentos && order.pagamentos.length > 0 ? (
                        <div className="space-y-2">
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/50">
                                  <TableHead>Data</TableHead>
                                  <TableHead>Forma de Pagamento</TableHead>
                                  <TableHead>Bandeira</TableHead>
                                  <TableHead className="text-center">Qtd. Parcelas</TableHead>
                                  <TableHead className="text-right">Valor</TableHead>
                                  <TableHead>NSU</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {order.pagamentos.map((pag, idx) => (
                                  <TableRow key={pag.id || idx}>
                                    <TableCell className="font-mono">
                                      {pag.data_pagamento 
                                        ? formatDateDisplay(pag.data_pagamento)
                                        : '-'}
                                    </TableCell>
                                    <TableCell>
                                      <Badge className={
                                        pag.forma_pagamento === 'Crédito' ? 'bg-purple-500/20 text-purple-500' :
                                        pag.forma_pagamento === 'Débito' ? 'bg-blue-500/20 text-blue-500' :
                                        pag.forma_pagamento === 'PIX' ? 'bg-green-500/20 text-green-500' :
                                        'bg-gray-500/20 text-gray-500'
                                      } variant="secondary">
                                        {pag.forma_pagamento}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      {pag.bandeira ? (
                                        <Badge className={
                                          pag.bandeira?.toLowerCase() === 'visa' ? 'bg-blue-400/20 text-blue-400 border-blue-400' :
                                          pag.bandeira?.toLowerCase() === 'mastercard' ? 'bg-orange-400/20 text-orange-400 border-orange-400' :
                                          pag.bandeira?.toLowerCase() === 'elo' ? 'bg-yellow-400/20 text-yellow-400 border-yellow-400' :
                                          'bg-gray-400/20 text-gray-400 border-gray-400'
                                        } variant="outline">
                                          {pag.bandeira}
                                        </Badge>
                                      ) : (
                                        <span className="text-muted-foreground">-</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center font-mono font-bold">
                                      {pag.parcelas || 1}x
                                    </TableCell>
                                    <TableCell className="text-right font-mono font-bold text-green-500">
                                      R$ {(pag.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </TableCell>
                                    <TableCell className="font-mono">
                                      {pag.nsu || '-'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {/* Linha de Total */}
                                <TableRow className="bg-muted/30 font-bold border-t-2">
                                  <TableCell colSpan={4} className="text-right">
                                    Total dos Pagamentos:
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-green-500 text-lg">
                                    R$ {(order.total_pago || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell></TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Nenhum pagamento registrado
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 pt-4 border-t">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(order)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleViewPDF(order.id)}>
                        <FileText className="h-4 w-4 mr-2" />
                        Visualizar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handlePrint(order.id)}>
                        <Printer className="h-4 w-4 mr-2" />
                        Baixar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setUploadingOrderId(order.id)}>
                        <Upload className="h-4 w-4 mr-2" />
                        Anexar
                      </Button>
                      {uploadingOrderId === order.id && (
                        <Input
                          type="file"
                          multiple
                          accept=".png,.jpg,.jpeg,.pdf"
                          onChange={(e) => handleUpload(order.id, e.target.files)}
                          className="max-w-xs"
                        />
                      )}
                      {order.status_financeiro !== 'Pago Total' && (
                        <Button size="sm" onClick={() => handleRegistrarPagamento(order.id)}>
                          <CreditCard className="h-4 w-4 mr-2" />
                          Registrar Pagamento
                        </Button>
                      )}
                      {/* Botão Criar OS */}
                      <Button size="sm" variant="outline" onClick={() => handleAbrirModalOS(order)} className="border-blue-500 text-blue-600 hover:bg-blue-500/10">
                        <ClipboardList className="h-4 w-4 mr-2" />
                        {order.itens_com_os?.length > 0 ? 'Nova OS' : 'Criar OS'}
                      </Button>
                      {order.ordem_servico_id && (
                        <Button size="sm" variant="outline" onClick={() => window.location.href = '/ordens-servico'} className="border-green-500 text-green-600 hover:bg-green-500/10">
                          <Eye className="h-4 w-4 mr-2" />
                          Ver OS
                        </Button>
                      )}
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(order.id)}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </Button>
                    </div>
                  </CardContent>
                </AccordionContent>
              </Card>
            </AccordionItem>
          ))}
        </Accordion>
        )}

        {filteredOrders.length === 0 && (
          <Card className="border-border">
            <CardContent className="py-16 text-center">
              <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Nenhum pedido encontrado' : 'Nenhum pedido cadastrado'}
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Pedido
              </Button>
            </CardContent>
          </Card>
        )}
          </TabsContent>

          <TabsContent value="orcamentos" className="space-y-6">
            <QuotesTab 
              products={products} 
              customers={customers} 
              onQuoteConverted={fetchData}
            />
          </TabsContent>

          <TabsContent value="projetados" className="space-y-6">
            <ProjetadosTab 
              customers={customers} 
              onProjetoConverted={fetchData}
            />
          </TabsContent>

          <TabsContent value="calculadoras" className="space-y-6">
            <CalculatorsTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Seleção de Itens para OS */}
      <Dialog open={osModalOpen} onOpenChange={setOsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-500" />
              Criar Ordem de Serviço
            </DialogTitle>
            <DialogDescription>
              {osSelectedPedido && (
                <>
                  <span className="font-medium">PED-{osSelectedPedido.numero_pedido}</span>
                  {' - '}
                  {osSelectedPedido.cliente_nome}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-3">
              Selecione os itens que serão incluídos na OS:
            </p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {osSelectedPedido?.produtos?.map((produto, idx) => {
                // Verificar se este item já tem OS vinculada
                const itemComOS = osSelectedPedido?.itens_com_os?.find(
                  i => i.produto_id === produto.produto_id
                );
                const jaTemOS = !!itemComOS;
                
                return (
                  <div 
                    key={idx}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      osSelectedItens.includes(idx) 
                        ? 'bg-blue-500/10 border-blue-500' 
                        : jaTemOS 
                          ? 'bg-amber-500/10 border-amber-500/50 hover:bg-amber-500/20' 
                          : 'hover:bg-muted/50'
                    }`}
                    onClick={() => toggleOsItem(idx)}
                  >
                    <input
                      type="checkbox"
                      checked={osSelectedItens.includes(idx)}
                      onChange={() => toggleOsItem(idx)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{produto.nome || 'Produto sem nome'}</p>
                        {jaTemOS && (
                          <span className="text-[10px] bg-amber-500/20 text-amber-700 px-1.5 py-0.5 rounded">
                            OS-{String(itemComOS.numero_os).padStart(4, '0')}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Qtd: {produto.quantidade} | {produto.tipo_madeira || 'Sem madeira definida'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {osSelectedPedido?.itens_com_os?.length > 0 && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <span className="inline-block w-2 h-2 bg-amber-500 rounded-full" />
                Itens com fundo amarelo já possuem OS vinculada
              </p>
            )}
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <span className="text-sm text-muted-foreground">
                {osSelectedItens.length} de {osSelectedPedido?.produtos?.length || 0} itens selecionados
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setOsSelectedItens(osSelectedPedido?.produtos?.map((_, i) => i) || [])}
                >
                  Selecionar Todos
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setOsSelectedItens([])}
                >
                  Limpar
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOsModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCriarOS} 
              disabled={osSelectedItens.length === 0 || creatingOS}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creatingOS ? 'Criando...' : `Criar OS (${osSelectedItens.length} itens)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
